import { useCallback, useEffect, useRef, useState } from 'react';
import { flushLocalDb, openLocalDb } from '../db/local.js';
import { getSupabase } from '../supabase.js';

export type WorkDraftKind = 'document_review' | 'recipe';
export type WorkDraftStatus = 'idle' | 'acquiring' | 'editable' | 'locked' | 'offline';

interface WorkDraftScope {
  accountId: string;
  tenantId: string;
  businessId: string;
  resourceKind: WorkDraftKind;
  resourceKey: string;
}

interface CachedDraft<T> {
  payload: T;
  updatedAt: string;
}

interface LeaseResponse<T> {
  acquired: boolean;
  revision: number;
  payload: T | null;
  updated_at?: string;
  lease_expires_at?: string;
}

function scopeId(scope: WorkDraftScope): string {
  return [scope.accountId, scope.tenantId, scope.businessId, scope.resourceKind, scope.resourceKey].join(':');
}

function hasPayload(value: unknown): boolean {
  return !!value && typeof value === 'object' && Object.keys(value as object).length > 0;
}

async function loadCachedDraft<T>(scope: WorkDraftScope): Promise<CachedDraft<T> | null> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT payload, updated_at FROM work_draft_cache_v1
      WHERE account_id = ? AND tenant_id = ? AND business_id = ?
        AND resource_kind = ? AND resource_key = ?`,
    [scope.accountId, scope.tenantId, scope.businessId, scope.resourceKind, scope.resourceKey],
  );
  const row = result.values?.[0] as { payload?: string; updated_at?: string } | undefined;
  if (!row) return null;
  try {
    return { payload: JSON.parse(row.payload ?? '{}') as T, updatedAt: row.updated_at ?? '' };
  } catch {
    return null;
  }
}

async function saveCachedDraft<T>(scope: WorkDraftScope, payload: T): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(
    `INSERT INTO work_draft_cache_v1(
       account_id, tenant_id, business_id, resource_kind, resource_key, payload, updated_at
     ) VALUES (?, ?, ?, ?, ?, ?, ?)
     ON CONFLICT(account_id, tenant_id, business_id, resource_kind, resource_key) DO UPDATE SET
       payload = excluded.payload, updated_at = excluded.updated_at`,
    [
      scope.accountId, scope.tenantId, scope.businessId, scope.resourceKind,
      scope.resourceKey, JSON.stringify(payload), new Date().toISOString(),
    ],
  );
  await flushLocalDb();
}

async function deleteCachedDraft(scope: WorkDraftScope): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(
    `DELETE FROM work_draft_cache_v1
      WHERE account_id = ? AND tenant_id = ? AND business_id = ?
        AND resource_kind = ? AND resource_key = ?`,
    [scope.accountId, scope.tenantId, scope.businessId, scope.resourceKind, scope.resourceKey],
  );
  await flushLocalDb();
}

interface DraftController<T> {
  update: (payload: T, shared: T) => void;
  save: (payload: T, shared: T) => Promise<void>;
  finish: (complete: boolean) => Promise<void>;
  assertLease: () => Promise<boolean>;
  resume: () => Promise<void>;
}

export function usePersistentWorkDraft<T>(args: {
  enabled: boolean;
  accountId: string | null;
  tenantId: string | null;
  businessId: string | null;
  resourceKind: WorkDraftKind;
  resourceKey: string | null;
  payload: T;
  sharedPayload?: T;
  onRestore: (payload: T) => void;
}): {
  status: WorkDraftStatus;
  guard: { resource_kind: WorkDraftKind; resource_key: string; lock_token: string } | null;
  assertLease: () => Promise<boolean>;
  resume: () => Promise<void>;
  complete: () => Promise<void>;
  release: () => Promise<void>;
} {
  const [status, setStatus] = useState<WorkDraftStatus>('idle');
  const [guard, setGuard] = useState<{ resource_kind: WorkDraftKind; resource_key: string; lock_token: string } | null>(null);
  const [visible, setVisible] = useState(document.visibilityState !== 'hidden');
  const [activation, setActivation] = useState(0);
  const controllerRef = useRef<DraftController<T> | null>(null);
  const latest = useRef(args);
  latest.current = args;
  useEffect(() => {
    const update = (): void => setVisible(document.visibilityState !== 'hidden');
    const resume = (event: PageTransitionEvent): void => { if (event.persisted) setActivation((value) => value + 1); update(); };
    document.addEventListener('visibilitychange', update);
    window.addEventListener('pageshow', resume);
    return () => { document.removeEventListener('visibilitychange', update); window.removeEventListener('pageshow', resume); };
  }, []);
  const scope = args.enabled && visible && args.accountId && args.tenantId && args.businessId && args.resourceKey
    ? { accountId: args.accountId, tenantId: args.tenantId, businessId: args.businessId,
        resourceKind: args.resourceKind, resourceKey: args.resourceKey } satisfies WorkDraftScope
    : null;
  const currentScopeId = scope ? scopeId(scope) : null;

  useEffect(() => {
    if (!scope) { setStatus('idle'); setGuard(null); return; }
    // Each activation owns its token, revision and queue. A delayed response
    // from the page being left cannot release or overwrite the next draft.
    const token = crypto.randomUUID();
    let active = true;
    let state: WorkDraftStatus = 'acquiring';
    let revision = 0;
    let hydrated = false;
    let chain = Promise.resolve();
    let finished: Promise<void> | null = null;
    let snapshot = { payload: args.payload, shared: args.sharedPayload ?? args.payload };
    const params = { p_tenant_id: scope.tenantId, p_business_id: scope.businessId,
      p_resource_kind: scope.resourceKind, p_resource_key: scope.resourceKey, p_lock_token: token };
    const publish = (next: WorkDraftStatus): void => {
      state = next;
      if (!active) return;
      setStatus(next);
      setGuard(next === 'editable' ? { resource_kind: scope.resourceKind, resource_key: scope.resourceKey, lock_token: token } : null);
    };
    const enqueue = (operation: () => Promise<void>): Promise<void> => {
      const pending = chain.then(operation);
      chain = pending.catch(() => undefined);
      return pending;
    };
    const save = async (payload: T, shared: T): Promise<void> => {
      if (!hydrated || (state !== 'editable' && state !== 'offline')) return;
      await saveCachedDraft(scope, payload);
      if (state !== 'editable') return;
      const { data, error } = await getSupabase().rpc('save_work_draft_v1', {
        ...params, p_expected_revision: revision, p_payload: shared, p_lease_seconds: 180,
      });
      if (error) {
        publish(error.message.includes('lease_lost') || error.message.includes('revision_conflict') ? 'locked' : 'offline');
        return;
      }
      revision = Number((data as { revision: number }).revision);
    };
    const acquire = async (): Promise<void> => {
      if (!active) return;
      const previous = state;
      if (previous === 'offline' && hydrated) await saveCachedDraft(scope, snapshot.payload);
      const cached = previous === 'acquiring' || previous === 'offline'
        ? await loadCachedDraft<T>(scope).catch(() => null) : null;
      if (active && previous === 'acquiring' && cached && hasPayload(cached.payload)) latest.current.onRestore(cached.payload);
      const { data, error } = await getSupabase().rpc('acquire_work_draft_v1', { ...params, p_lease_seconds: 180 });
      // Even after leaving, retain acquisition outcome so finish releases it.
      if (error) { hydrated = active; publish('offline'); return; }
      const lease = data as LeaseResponse<T>;
      revision = Number(lease.revision ?? 0);
      if (active && previous !== 'editable' && hasPayload(lease.payload)) {
        const newerCache = cached && Date.parse(cached.updatedAt) > Date.parse(lease.updated_at ?? '');
        if (!newerCache || !lease.acquired) latest.current.onRestore(lease.payload as T);
      }
      hydrated = active;
      publish(lease.acquired ? 'editable' : 'locked');
    };
    publish('acquiring');
    const controller: DraftController<T> = {
      update: (payload, shared) => { snapshot = { payload, shared }; },
      save: (payload, shared) => enqueue(async () => { if (active) await save(payload, shared); }),
      finish: (complete) => {
        if (finished) return finished;
        const { payload, shared } = snapshot;
        active = false;
        window.clearInterval(timer);
        finished = enqueue(async () => {
          let saveFailure: unknown;
          try { if (!complete) await save(payload, shared); } catch (error) { saveFailure = error; }
          const { error } = await getSupabase().rpc('release_work_draft_v1', { ...params, p_complete: complete });
          if (error) throw error;
          if (saveFailure) throw saveFailure;
          if (complete) await deleteCachedDraft(scope);
        });
        if (controllerRef.current === controller) {
          controllerRef.current = null;
          setStatus('idle'); setGuard(null);
        }
        return finished;
      },
      resume: () => enqueue(async () => {
        if (!active || state !== 'locked') return;
        const { data, error } = await getSupabase().rpc('resume_work_draft_v1', {
          ...params, p_expected_revision: revision,
        });
        if (error) {
          // Refresh changed server content before the user decides to retry.
          if (error.message.includes('revision_conflict')) await acquire();
          throw error;
        }
        const lease = data as LeaseResponse<T>;
        revision = Number(lease.revision);
        if (active && hasPayload(lease.payload)) latest.current.onRestore(lease.payload as T);
        publish('editable');
      }),
      assertLease: async () => {
        await chain;
        if (!active || state !== 'editable') return false;
        const { data, error } = await getSupabase().rpc('assert_work_draft_lease_v1', params);
        if (active && !error && data === true) return true;
        publish(error?.message.includes('lease_lost') ? 'locked' : 'offline');
        return false;
      },
    };
    controllerRef.current = controller;
    void enqueue(acquire).catch(() => publish('offline'));
    // Locked readers also retry, so releasing/expiring the other lease is visible.
    const timer = window.setInterval(() => { void enqueue(acquire).catch(() => publish('offline')); }, 15_000);
    const leave = (): void => { void controller.finish(false).catch(() => undefined); };
    window.addEventListener('pagehide', leave);
    return () => {
      window.removeEventListener('pagehide', leave);
      leave();
    };
    // Acquisition follows scalar scope identity; payload edits are saved below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScopeId, activation]);

  useEffect(() => {
    const controller = controllerRef.current;
    controller?.update(args.payload, args.sharedPayload ?? args.payload);
    if (!controller || (status !== 'editable' && status !== 'offline')) return;
    const timer = window.setTimeout(() => {
      void controller.save(args.payload, args.sharedPayload ?? args.payload).catch(() => undefined);
    }, 600);
    return () => window.clearTimeout(timer);
  }, [args.payload, args.sharedPayload, status]);

  const assertLease = useCallback(() => controllerRef.current?.assertLease() ?? Promise.resolve(false), []);
  const complete = useCallback(() => controllerRef.current?.finish(true) ?? Promise.resolve(), []);
  const release = useCallback(() => controllerRef.current?.finish(false) ?? Promise.resolve(), []);
  const resume = useCallback(() => controllerRef.current?.resume() ?? Promise.resolve(), []);
  return { status, guard, assertLease, complete, release, resume };
}
